import React from "react";
import { Star, Phone, ArrowRight, ShieldCheck, Clock, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SITE } from "@/lib/site";

export const Hero = ({ onQuoteClick }) => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden"
    >
      {/* Background blueprint dots */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.5]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(37,99,235,0.18) 1px, transparent 0)",
          backgroundSize: "28px 28px",
        }}
      />
      <div
        aria-hidden
        className="absolute -top-40 -right-32 h-[480px] w-[480px] rounded-full bg-blue-100/60 blur-3xl -z-10"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left */}
        <div className="reveal-up">
          <div className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 ring-1 ring-inset ring-blue-700/10 mb-6">
            <span className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
              ))}
            </span>
            <span className="text-xs font-bold text-blue-700">
              {SITE.rating.toFixed(1)} on Google · {SITE.reviewCountLabel} reviews
            </span>
          </div>

          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-slate-900">
            Restore your home's
            <span className="block text-blue-600">true colors.</span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-slate-600 font-medium leading-relaxed max-w-xl">
            Professional soft-wash, driveway, and gutter cleaning across Ohio.
            Honest pricing, careful crews, and a finish that turns heads —
            guaranteed.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Button
              data-testid="hero-quote-btn"
              onClick={onQuoteClick}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl h-12 px-6 shadow-md hover:-translate-y-0.5 transition-all"
            >
              Get a Free Quote
              <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
            <a
              href={SITE.phoneHref}
              data-testid="hero-phone-btn"
              className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-white border-2 border-slate-200 text-slate-900 font-bold hover:border-blue-600 hover:text-blue-600 transition-all"
            >
              <Phone className="h-4 w-4" />
              {SITE.phone}
            </a>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-md">
            <Stat icon={<ShieldCheck className="h-4 w-4" />} label="Insured & Bonded" />
            <Stat icon={<Clock className="h-4 w-4" />} label="Free Quote in 1 hr" />
            <Stat icon={<MapPin className="h-4 w-4" />} label="Serving All Ohio" />
          </div>
        </div>

        {/* Right — image card */}
        <div className="relative reveal-up" style={{ animationDelay: "0.15s" }}>
          <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-900/5 aspect-[4/5] md:aspect-[3/4]">
            <img
              src="https://customer-assets.emergentagent.com/job_clearview-services/artifacts/aimy9eyc_IMG_4194.jpeg"
              alt="Beautifully cleaned wooden deck after ClearView pressure washing"
              className="absolute inset-0 h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
            <div className="absolute top-4 left-4 flex gap-2">
              <span className="px-2.5 py-1 rounded-full bg-blue-600 text-[10px] font-black tracking-widest text-white uppercase">
                Real Job · Ohio
              </span>
            </div>
          </div>

          {/* Floating review badge */}
          <div className="hidden md:block absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl ring-1 ring-slate-200 p-4 max-w-[260px]">
            <div className="flex items-center gap-1 mb-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              ))}
              <span className="ml-1 text-xs font-bold text-slate-900">5.0</span>
            </div>
            <p className="text-xs text-slate-700 font-medium leading-snug">
              "Our siding looked brand new. On time, careful, and worth every penny."
            </p>
            <p className="mt-2 text-[10px] uppercase tracking-wider font-bold text-slate-500">
              — Sarah M., Akron OH
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

const Stat = ({ icon, label }) => (
  <div className="flex items-start gap-2">
    <div className="h-7 w-7 rounded-lg bg-blue-50 text-blue-600 grid place-items-center shrink-0">
      {icon}
    </div>
    <span className="text-xs font-semibold text-slate-700 leading-tight">
      {label}
    </span>
  </div>
);
